import { __decorate } from "tslib";
import React, { Children } from 'react';
import classNames from 'classnames';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import isString from 'lodash/isString';
import { computed, observable, runInAction, action, isArrayLike } from 'mobx';
import { observer } from 'mobx-react';
import isPromise from 'is-promise';
import { isFragment as isReactFragment } from 'react-is';
import { ProgressType } from '../../../es/progress/enum';
import Icon from '../icon';
import FormContext from '../form/FormContext';
import Progress from '../progress';
import Ripple from '../ripple';
import { ButtonTooltip, ButtonType, FuncType } from './enum';
import { DataSetStatus } from '../data-set/enum';
import { Size, WaitType } from '../core/enum';
import DataSetComponent from '../data-set/DataSetComponent';
import autobind from '../_util/autobind';
import { hide, show } from '../tooltip/singleton';
import isOverflow from '../overflow-tip/util';
import getReactNodeText from '../_util/getReactNodeText';
import { cloneElement } from '../_util/reactNode';
const rxTwoCNChar = /^[\u4e00-\u9fa5]{2}$/;
const isTwoCNChar = rxTwoCNChar.test.bind(rxTwoCNChar);
// Insert one space between two chinese characters automatically.
function insertSpace(child, needInserted) {
    // Check the child if is undefined or null.
    if (child == null) {
        return;
    }
    const SPACE = needInserted ? ' ' : '';
    // strictNullChecks oops.
    if (typeof child !== 'string' &&
        typeof child !== 'number' &&
        isString(child.type) &&
        isTwoCNChar(child.props.children)) {
        return cloneElement(child, {
            children: child.props.children.split('').join(SPACE),
        });
    }
    if (typeof child === 'string') {
        return isTwoCNChar(child) ? React.createElement("span", null, child.split('').join(SPACE)) : React.createElement("span", null, child);
    }
    if (isReactFragment(child)) {
        return React.createElement("span", null, child);
    }
    return child;
}
function spaceChildren(children, needInserted) {
    let isPrevChildPure = false;
    const childList = [];
    React.Children.forEach(children, child => {
        const type = typeof child;
        const isCurrentChildPure = type === 'string' || type === 'number';
        if (isPrevChildPure && isCurrentChildPure) {
            const lastIndex = childList.length - 1;
            const lastChild = childList[lastIndex];
            childList[lastIndex] = `${lastChild}${child}`;
        }
        else {
            childList.push(child);
        }
        isPrevChildPure = isCurrentChildPure;
    });
    // Pass to React.Children.map to auto fill key
    return React.Children.map(childList, child => insertSpace(child, needInserted));
}
let Button = class Button extends DataSetComponent {
    constructor(props, context) {
        super(props, context);
        this.handleClickWait = this.getHandleClick(props);
    }
    static get contextType() {
        return FormContext;
    }
    get loading() {
        const { type, dataSet, loading } = this.observableProps;
        return (loading ||
            (type === ButtonType.submit && !!dataSet && dataSet.status === DataSetStatus.submitting));
    }
    set loading(loading) {
        runInAction(() => {
            this.observableProps.loading = loading;
        });
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            dataSet: 'dataSet' in props ? props.dataSet : context.dataSet,
            loading: 'loading' in props ? props.loading : this.observableProps ? this.observableProps.loading : false,
            type: props.type,
            disabled: context.disabled || props.disabled,
        };
    }
    fixTwoCNChar() {
        // For HOC usage
        const autoInsertSpace = this.getContextConfig('autoInsertSpaceInButton') !== false;
        if (!this.element || !autoInsertSpace) {
            return;
        }
        const buttonText = this.element.textContent;
        if (this.isNeedInserted() && isTwoCNChar(buttonText)) {
            if (!this.hasTwoCNChar) {
                this.hasTwoCNChar = true;
            }
        }
        else if (this.hasTwoCNChar) {
            this.hasTwoCNChar = false;
        }
    }
    componentDidMount() {
        super.componentDidMount();
        this.fixTwoCNChar();
    }
    componentWillReceiveProps(nextProps, nextContext) {
        super.componentWillReceiveProps(nextProps, nextContext);
        const { wait, waitType } = this.props;
        if (wait !== nextProps.wait || waitType !== nextProps.waitType) {
            this.handleClickWait = this.getHandleClick(nextProps);
        }
    }
    componentWillUnmount() {
        this.handleClickWait.cancel();
        if (this.isTooltipShown) {
            hide();
            delete this.isTooltipShown;
        }
    }
    getHandleClick(props) {
        const { wait, waitType } = props;
        if (wait && waitType) {
            const options = { leading: true, trailing: true };
            if (waitType === WaitType.throttle) {
                options.trailing = false;
                options.maxWait = wait;
            }
            else if (waitType === WaitType.debounce) {
                options.leading = false;
            }
            return debounce(this.handleClick, wait, options);
        }
        return debounce(this.handleClick, 0);
    }
    handleClickIfBubble(e) {
        const { wait, waitType } = this.props;
        if (wait && waitType) {
            e.stopPropagation();
            this.handleClickWait(e);
        }
        else {
            this.handleClick(e);
        }
    }
    async handleClick(e) {
        const onButtonClick = this.context.getConfig('onButtonClick');
        if (onButtonClick && e) {
            const { target } = e;
            const { children, icon } = this.props;
            const promise = Promise.resolve(target && target.textContent || getReactNodeText(children));
            promise.then(title => onButtonClick({ icon, title }));
        }
        const { onClick } = this.props;
        if (onClick) {
            const afterClick = onClick(e);
            if (isPromise(afterClick)) {
                try {
                    this.loading = true;
                    await afterClick;
                }
                finally {
                    this.loading = false;
                }
            }
        }
    }
    handleMouseEnter(e) {
        const { getTooltip, getTooltipTheme, getTooltipPlacement } = this.context;
        const { tooltip = getTooltip('button'), children } = this.props;
        const { element } = this;
        if (tooltip === ButtonTooltip.always || (tooltip === ButtonTooltip.overflow && isOverflow(element))) {
            show(element, {
                title: children,
                theme: getTooltipTheme('button'),
                placement: getTooltipPlacement('button'),
            });
            this.isTooltipShown = true;
        }
        else if (isArrayLike(tooltip)) {
            const tooltipType = tooltip[0];
            const buttonTooltipProps = tooltip[1] || {};
            const duration = (buttonTooltipProps.mouseEnterDelay || 0.1) * 1000;
            if (tooltipType === ButtonTooltip.always || (tooltipType === ButtonTooltip.overflow && isOverflow(element))) {
                show(element, {
                    theme: getTooltipTheme('button'),
                    placement: getTooltipPlacement('button'),
                    title: buttonTooltipProps.title ? buttonTooltipProps.title : children,
                    ...buttonTooltipProps,
                }, duration);
                this.isTooltipShown = true;
            }
        }
        const { onMouseEnter = noop } = this.props;
        onMouseEnter(e);
    }
    handleMouseLeave(e) {
        const { getTooltip } = this.context;
        const { tooltip = getTooltip('button') } = this.props;
        if (isArrayLike(tooltip)) {
            const buttonTooltipProps = tooltip[1] || {};
            const duration = (buttonTooltipProps.mouseLeaveDelay || 0.1) * 1000;
            hide(duration);
        }
        else {
            hide();
        }
        const { onMouseLeave = noop } = this.props;
        onMouseLeave(e);
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'icon',
            'funcType',
            'color',
            'loading',
            'wait',
            'waitType',
            'tooltip',
            'block',
            'onClick',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        const { getTooltip } = this.context;
        const { tooltip = getTooltip('button') } = this.props;
        if (!this.disabled && !this.loading) {
            otherProps.onClick = this.handleClickIfBubble;
        }
        if ((isString(tooltip) && [ButtonTooltip.always, ButtonTooltip.overflow].includes(tooltip)) ||
            isArrayLike(tooltip) && [ButtonTooltip.always, ButtonTooltip.overflow].includes(tooltip[0])) {
            otherProps.onMouseEnter = this.handleMouseEnter;
            otherProps.onMouseLeave = this.handleMouseLeave;
        }
        return otherProps;
    }
    isNeedInserted() {
        const { props: { funcType = this.getContextConfig('buttonFuncType'), children, icon, }, } = this;
        return Children.count(children) === 1 && !icon && !(funcType === FuncType.link || funcType === FuncType.flat);
    }
    getClassName(...props) {
        const { prefixCls, props: { color = this.getContextConfig('buttonColor'), funcType = this.getContextConfig('buttonFuncType'), children, icon, block, }, } = this;
        const childrenCount = Children.count(children);
        const autoInsertSpace = this.getContextConfig('autoInsertSpaceInButton') !== false;
        return super.getClassName({
            [`${prefixCls}-${funcType}`]: funcType,
            [`${prefixCls}-${color}`]: color,
            [`${prefixCls}-icon-only`]: icon
                ? childrenCount === 0 || children === false
                : childrenCount === 1 && children.type && children.type.__C7N_ICON,
            [`${prefixCls}-block`]: block,
            [`${prefixCls}-loading`]: this.loading,
            [`${prefixCls}-two-chinese-chars`]: this.hasTwoCNChar && autoInsertSpace,
        }, ...props);
    }
    render() {
        const { children, icon, href, funcType } = this.props;
        const { loading, disabled } = this;
        const autoInsertSpace = this.getContextConfig('autoInsertSpaceInButton') !== false;
        const buttonIcon = loading ? (React.createElement(Progress, { key: "loading", type: ProgressType.loading, size: Size.small })) : (icon && React.createElement(Icon, { type: icon }));
        const hasString = Children.toArray(children).some(child => isString(child));
        const Cmp = href ? 'a' : 'button';
        const props = this.getMergedProps();
        const { onMouseEnter, onMouseLeave } = props;
        const tooltipWrapper = disabled && !href && (onMouseEnter || onMouseLeave);
        const omits = [];
        if (tooltipWrapper) {
            omits.push('className', 'style');
        }
        if (href) {
            omits.push('type');
            if (disabled || loading) {
                omits.push('href');
            }
        }
        const kids = children || children === 0
            ? spaceChildren(children, this.isNeedInserted() && autoInsertSpace)
            : null;
        const button = (React.createElement(Ripple, { disabled: disabled || funcType === FuncType.link },
            React.createElement(Cmp, Object.assign({}, omit(props, omits)),
                buttonIcon,
                hasString ? React.createElement("span", null, kids) : kids)));
        return tooltipWrapper ? (React.createElement("span", { className: classNames(props.className, `${this.prefixCls}-disabled-wrapper`), style: props.style, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }, button)) : button;
    }
};
Button.displayName = 'Button';
// eslint-disable-next-line camelcase
Button.__PRO_BUTTON = true;
Button.defaultProps = {
    suffixCls: 'btn',
    type: ButtonType.button,
    waitType: WaitType.throttle,
};
__decorate([
    observable
], Button.prototype, "hasTwoCNChar", void 0);
__decorate([
    computed
], Button.prototype, "loading", null);
__decorate([
    autobind,
    action
], Button.prototype, "fixTwoCNChar", null);
__decorate([
    autobind
], Button.prototype, "handleClickIfBubble", null);
__decorate([
    autobind
], Button.prototype, "handleClick", null);
__decorate([
    autobind
], Button.prototype, "handleMouseEnter", null);
__decorate([
    autobind
], Button.prototype, "handleMouseLeave", null);
Button = __decorate([
    observer
], Button);
export default Button;
